/**
 * Patient timeline — aggregated chronological event stream.
 *
 * Surface for "what happened with this patient, in order?" — visits,
 * prescriptions, lab orders/results, vitals, invoices, appointments.
 * Each event is keyed by (type, source_id) so the frontend can de-dupe
 * and link back to the source record.
 *
 * Why one endpoint instead of many: a doctor reviewing a patient wants
 * a single scroll, not 7 tabs. The aggregation is read-only, the source
 * tables stay authoritative.
 */
import { z } from "zod";

export const timelineEventTypeSchema = z.enum([
	"appointment",
	"visit",
	"prescription",
	"lab_order",
	"lab_result",
	"radiology",
	"vitals",
	"invoice",
]);
export type TimelineEventType = z.infer<typeof timelineEventTypeSchema>;

export interface TimelineEvent {
	/** Stable composite id: `${type}:${source_id}`. Safe for React keys. */
	id: string;
	type: TimelineEventType;
	source_id: string;
	occurred_at: string;
	/** Short bilingual-aware label. The backend picks the right side. */
	title: string;
	/** Optional one-line summary (status, count, total, etc). */
	summary: string | null;
	/** Optional doctor full name (if the event is doctor-authored). */
	doctor_name: string | null;
	/** Tags for color/icon hints — frontend maps these. */
	tags: string[];
}

export const timelineQuerySchema = z.object({
	types: z
		.string()
		.optional()
		.transform((s) =>
			s
				? s
						.split(",")
						.map((x) => x.trim())
						.filter(Boolean)
				: undefined,
		),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	limit: z.coerce.number().int().positive().max(500).default(200),
});
export type TimelineQuery = z.infer<typeof timelineQuerySchema>;

export interface TimelineResult {
	events: TimelineEvent[];
	/** Per-type counts across the *entire* patient history (not just window). */
	counts: Record<TimelineEventType, number>;
}
